/* Shares moved from one of your accounts to another.
 *
 * Until now the only way was the Account dropdown in the lot editor, which
 * moves the whole lot, has no fee, records nothing, and — verified by
 * experiment — silently produces two `Holding` rows for one ticker in one
 * account when the destination already holds it. That is the shape the model
 * forbids: `basisFromTrades` can no longer say which row a trade belongs to,
 * and `ReduceClose` renders two options with the same value so the second row's
 * shares cannot be sold at all.
 *
 * ── what a transfer is, and is not ────────────────────────────────────────
 *
 * It is **not a sale.** Nothing was disposed of, no gain was realised, no price
 * was struck. Writing a `Trade` for either leg would put a gain on the tax
 * screen that nobody earned.
 *
 * It is **not a cash flow.** No money entered or left the book, so money paid
 * in must not move — the argument is made at length in `state/convertCash.ts`
 * and it is the same argument.
 *
 * It is **not a correction.** The lot editor's own note says it "records no
 * trade and no gain", which is right for a lot filed against the wrong broker
 * and wrong for shares that actually moved on a date, for a fee.
 *
 * ── conservation ─────────────────────────────────────────────────────────
 *
 * Two quantities are conserved and the tests say so. **Shares**: what leaves
 * the source arrives at the destination, to the last decimal, after a single
 * rounding. **Recorded cost**: the cost the two lots carry between them is the
 * same before and after, which is what makes this not a disposal.
 *
 * The cost that travels is the source lot's average at the moment of the move,
 * and it is written on to the record because it cannot be recovered afterwards
 * — the source lot's average changes the next time anything is bought into it.
 * `avgCost === 0` means unknown, and unknown survives the journey as unknown.
 *
 * ── the fee ──────────────────────────────────────────────────────────────
 *
 * It leaves cash and touches nothing else. Not the cost basis: these shares
 * were already acquired and what it costs to move them is not what they cost.
 * Not a realised result: nothing was realised. Not money paid in: nothing was
 * paid in or taken out of the book. It is denominated in the paying account's
 * own currency so it never needs converting, and it records a `CashEffect` so
 * that correcting or deleting the transfer can undo exactly what it did.
 *
 * ── pure over the whole book ─────────────────────────────────────────────
 *
 * The shape `renameTicker` already uses, for the same reason: two lot writes
 * and a cash write have to land together or not at all, and sequential store
 * puts cannot promise that — a failure between them loses or duplicates the
 * shares permanently. The caller commits the returned book in one transaction. */

import { mergeLots } from './addToLot';
import { newId } from '../lib/id';
import type { BookData } from '../data/db';
import type { AccountRecord, CashEffect, Holding, ShareTransfer } from '../types';

/** Rounding the split once, rather than deriving each half. Two independently
    derived halves can fail to sum, and the book's total moves by the drift. */
const PLACES = 8;
/** Fractional holdings are ordinary; a millionth of a share is not a shortfall. */
const HAIR = 1e-9;

export type TransferRefusal =
  | 'nothing-to-move'
  | 'same-account'
  | 'no-lot'
  | 'not-enough-shares'
  | 'futures-destination'
  | 'currency-mismatch';

export interface TransferOutcome {
  data: BookData;
  record: ShareTransfer;
  /** Told, never refused — the `overdrawn` precedent in `convertCash`. */
  flags: {
    /** The cost did not survive, because one side of the merge had none. */
    costUnknown: boolean;
    /** Leaving or entering a tax-shielded account. The book applies the shelter
        to the account a sale happens in, so this changes how later gains on
        these shares are reported here. */
    shelterChange: 'leaving' | 'entering' | null;
    /** The fee took the paying account below zero. */
    feeOverdraws: boolean;
    /** The two lots disagree on the fetch symbol. Not a refusal — nothing in
        the cost arithmetic reads it — but worth saying. */
    symbolsDiffer: boolean;
  };
}

export interface TransferRequest {
  ticker: string;
  fromAccount: string;
  toAccount: string;
  shares: number;
  at: string;
  fee?: number;
  feeAccount?: string;
  /** The shares have already been moved by hand — through the old Account
      dropdown, most likely — so write the record and leave the lots alone. The
      same guard `closePerp` carries as `walletAlreadySettled`. */
  alreadyMoved?: boolean;
  note?: string;
  /** Supplied so the function stays pure; the caller has it to hand. */
  id?: string;
}

export function refuseReason(
  data: BookData, accounts: AccountRecord[], req: TransferRequest,
): TransferRefusal | null {
  if (!(req.shares > 0)) return 'nothing-to-move';
  /* Not a no-op: both writes would be computed from the same snapshot under the
     same holding id, and the second would overwrite the first — minting shares
     out of nothing. */
  if (req.fromAccount === req.toAccount) return 'same-account';

  /* A move already applied by hand has usually taken the source lot with it —
     the old Account dropdown moves the whole row — so requiring one here would
     refuse exactly the case the option exists for. The shares are at the
     destination and the record is catching up with them. */
  const source = lotIn(data.holdings, req.ticker, req.fromAccount);
  if (!req.alreadyMoved) {
    if (!source) return 'no-lot';
    if (req.shares > source.shares + HAIR) return 'not-enough-shares';
  } else if (!source && !lotIn(data.holdings, req.ticker, req.toAccount)) {
    return 'no-lot';
  }

  if (accounts.find((a) => a.id === req.toAccount)?.isFutures) return 'futures-destination';

  const dest = lotIn(data.holdings, req.ticker, req.toAccount);
  /* Averaging across two units of money produces a number that reads as a cost
     and is not one, and the whole position's basis is converted at one lot's
     currency with nothing downstream to catch it. */
  if (dest && source && dest.currency !== source.currency) return 'currency-mismatch';

  return null;
}

function lotIn(holdings: Holding[], ticker: string, account: string): Holding | undefined {
  return holdings.find((h) => h.ticker === ticker && h.account === account);
}

export function transferShares(
  data: BookData,
  accounts: AccountRecord[],
  req: TransferRequest,
): TransferOutcome | null {
  if (refuseReason(data, accounts, req)) return null;

  const dest = lotIn(data.holdings, req.ticker, req.toAccount);
  /* Where the move has already been applied the source row may be gone, and the
     shares are sitting at the destination — so that is where the cost and the
     listing come from. Everything below reads `source` for those two facts
     only; the lot writes are skipped in that case. */
  const source = lotIn(data.holdings, req.ticker, req.fromAccount) ?? dest!;
  const moving = req.shares;
  const carried = source.avgCost > 0 ? source.avgCost : undefined;

  const record: ShareTransfer = {
    id: req.id ?? newId('xfer'),
    at: req.at,
    ticker: req.ticker,
    fromAccount: req.fromAccount,
    toAccount: req.toAccount,
    shares: moving,
    currency: source.currency,
  };
  if (carried !== undefined) record.costPerShare = carried;
  /* The destination's average as it stands now, so undoing this can put it
     back. Recorded even when it is zero-meaning-unknown, because "there was a
     lot and its cost was unknown" and "there was no lot" undo differently. */
  if (dest) record.destAvgBefore = dest.avgCost;
  if (req.note?.trim()) record.note = req.note.trim();

  let holdings = data.holdings;

  if (!req.alreadyMoved) {
    const left = Number((source.shares - moving).toFixed(PLACES));
    /* A reduction never moves the average — the same rule a sale follows. */
    const sourceAfter: Holding | null = left > HAIR ? { ...source, shares: left } : null;

    const destAfter: Holding = dest
      ? mergeLots(dest, { shares: moving, costPerShare: carried })!
      : {
        /* Copied verbatim, never re-derived from the destination account. The
           whole position takes its fetch symbol, asset kind and cost currency
           from whichever lot happens to sort first, so a lot with a blank or
           guessed symbol can unprice the position and stop history being
           written. `openedAt` comes from the source for the same class of
           reason: the rebuilt value history starts at the earliest date still
           held, and stamping today would silently shorten the chart. */
        id: newId('hold'),
        ticker: source.ticker,
        yahooSymbol: source.yahooSymbol,
        account: req.toAccount,
        shares: moving,
        avgCost: carried ?? 0,
        currency: source.currency,
        openedAt: source.openedAt,
        ...(source.name !== undefined ? { name: source.name } : {}),
        ...(source.assetKind !== undefined ? { assetKind: source.assetKind } : {}),
      };

    holdings = data.holdings
      .filter((h) => h.id !== source.id && h.id !== dest?.id)
      .concat(sourceAfter ? [sourceAfter] : [])
      .concat([destAfter]);
  }

  /* The fee, out of cash and nowhere else. */
  let cash = data.cash;
  let feeOverdraws = false;
  const fee = Math.abs(req.fee ?? 0);
  const feeAccount = req.feeAccount ?? req.fromAccount;
  if (fee > 0) {
    const held = data.cash.find((c) => c.account === feeAccount);
    const currency = held?.currency
      ?? accounts.find((a) => a.id === feeAccount)?.currency
      ?? 'USD';
    const after = (held?.amount ?? 0) - fee;
    feeOverdraws = after < 0;
    const effect: CashEffect = { account: feeAccount, amount: -fee, currency };
    record.fee = fee;
    record.feeAccount = feeAccount;
    record.cashEffect = effect;
    cash = held
      ? data.cash.map((c) => (c.account === feeAccount ? { ...c, amount: after } : c))
      : [...data.cash, { account: feeAccount, amount: after, currency: currency as never }];
  }

  const shielded = (id: string) => accounts.find((a) => a.id === id)?.taxShielded === true;
  const wasShielded = shielded(req.fromAccount);
  const goesShielded = shielded(req.toAccount);

  return {
    data: { ...data, holdings, cash, transfers: [...(data.transfers ?? []), record] },
    record,
    flags: {
      costUnknown: carried === undefined || (dest ? !(dest.avgCost > 0) : false),
      shelterChange: wasShielded && !goesShielded ? 'leaving'
        : !wasShielded && goesShielded ? 'entering' : null,
      feeOverdraws,
      symbolsDiffer: !!dest && dest.yahooSymbol !== source.yahooSymbol,
    },
  };
}

/** Two rows for one ticker in one account — the state the old Account dropdown
    has been producing. Reported so a screen can offer to join them, rather than
    merged on load: that would be stored data changing behind the user's back,
    and where the two rows disagree on currency there is no correct answer. */
export function duplicateLots(holdings: Holding[]): Holding[][] {
  const byKey = new Map<string, Holding[]>();
  for (const h of holdings) {
    const key = `${h.ticker}|${h.account}`;
    byKey.set(key, [...(byKey.get(key) ?? []), h]);
  }
  return [...byKey.values()].filter((rows) => rows.length > 1);
}

/** Joins duplicate rows into the one row the model allows, oldest first so the
    surviving row keeps the earliest `openedAt`. Null when they cannot be
    joined, which today means they disagree on currency. */
export function joinLots(rows: Holding[]): Holding | null {
  if (rows.length < 2) return null;
  const ordered = [...rows].sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  if (ordered.some((r) => r.currency !== ordered[0]!.currency)) return null;

  let merged = ordered[0]!;
  for (const row of ordered.slice(1)) {
    const next = mergeLots(merged, {
      shares: row.shares,
      costPerShare: row.avgCost > 0 ? row.avgCost : undefined,
    });
    if (!next) return null;
    merged = next;
  }
  return merged;
}


/* Undoing one, and restating one.
 *
 * This is where a transfer parts company with a currency conversion, which is
 * removed by deleting the row and leaving the balances alone. A conversion is
 * inert once written; a transfer is not — `fifoRealised` reads it to know which
 * account a parcel sits in, so a record changed without moving the shares back
 * leaves every derivation describing one book and every screen showing another.
 * So a correction reverses and re-states in one write, and a deletion reverses.
 *
 * The house rule survives: the record still describes exactly one event, and it
 * is never re-run as a second one.
 *
 * ── when it refuses ──────────────────────────────────────────────────────
 *
 * When the destination no longer holds what the transfer put there. Putting
 * shares back that have since been sold, or moved on again, is arithmetic this
 * book cannot honestly do — the same reason `ImportBatch` refuses to undo
 * against holdings something else has touched. It says which lot is short
 * rather than netting the difference into a number nobody can check. */

export type ReverseRefusal = 'destination-short' | 'no-record';

export interface ReverseOutcome {
  data: BookData;
  /** How many the destination actually holds, when it is short. */
  short?: { account: string; has: number; needs: number };
}

/** Puts the shares back where they came from, at the cost that travelled, and
    undoes the fee. Null with a reason when it cannot be done honestly. */
export function reverseTransfer(
  data: BookData,
  record: ShareTransfer,
): { outcome: ReverseOutcome } | { refusal: ReverseRefusal; short?: ReverseOutcome['short'] } {
  const dest = lotIn(data.holdings, record.ticker, record.toAccount);
  const has = dest?.shares ?? 0;
  if (has + HAIR < record.shares) {
    return {
      refusal: 'destination-short',
      short: { account: record.toAccount, has, needs: record.shares },
    };
  }

  /* Off the destination, back on to the source, at the cost the record carries
     — not at whatever the destination's average has become since, which would
     move cost between two lots that never traded.
     *
     * The destination's own average goes back to what it was before the merge.
     * Reducing the count and leaving the average alone leaks the arriving
     * shares' cost into the lot that no longer holds them: measured on a live
     * book, correcting a 200-share move down to 80 left XTB USD at 17.0406
     * where the honest figure is 16.9168, and 25.77 of recorded cost appeared
     * out of nothing. */
  const left = Number((has - record.shares).toFixed(PLACES));
  const destAfter: Holding | null = left > HAIR
    ? { ...dest!, shares: left, avgCost: record.destAvgBefore ?? dest!.avgCost }
    : null;

  const source = lotIn(data.holdings, record.ticker, record.fromAccount);
  const sourceAfter: Holding = source
    ? mergeLots(source, { shares: record.shares, costPerShare: record.costPerShare })!
    : {
      id: newId('hold'),
      ticker: record.ticker,
      yahooSymbol: dest!.yahooSymbol,
      account: record.fromAccount,
      shares: record.shares,
      avgCost: record.costPerShare ?? 0,
      currency: record.currency,
      openedAt: dest!.openedAt,
      ...(dest!.name !== undefined ? { name: dest!.name } : {}),
      ...(dest!.assetKind !== undefined ? { assetKind: dest!.assetKind } : {}),
    };

  const holdings = data.holdings
    .filter((h) => h.id !== dest?.id && h.id !== source?.id)
    .concat(destAfter ? [destAfter] : [])
    .concat([sourceAfter]);

  /* The fee comes back to the account it was taken from, in the currency and at
     the amount that was actually applied — never at today's rate. */
  let cash = data.cash;
  const undo = record.cashEffect;
  if (undo) {
    const held = data.cash.find((c) => c.account === undo.account);
    cash = held
      ? data.cash.map((c) => (c.account === undo.account
        ? { ...c, amount: c.amount - undo.amount } : c))
      : [...data.cash, {
        account: undo.account, amount: -undo.amount, currency: undo.currency as never,
      }];
  }

  return {
    outcome: {
      data: {
        ...data,
        holdings,
        cash,
        transfers: (data.transfers ?? []).filter((x) => x.id !== record.id),
      },
    },
  };
}

/** Restates one: reverses what it did, then applies what it now says. The two
    land in a single returned book, so a failure between them is not possible. */
export function correctTransfer(
  data: BookData,
  accounts: AccountRecord[],
  record: ShareTransfer,
  next: Omit<TransferRequest, 'id'>,
): TransferOutcome | { refusal: ReverseRefusal | TransferRefusal; short?: ReverseOutcome['short'] } {
  const undone = reverseTransfer(data, record);
  if ('refusal' in undone) return undone;

  const refusal = refuseReason(undone.outcome.data, accounts, { ...next, id: record.id });
  if (refusal) return { refusal };

  /* The same id, so the record is restated rather than replaced — a correction
     that changed the id would read as a delete and a new entry in any log. */
  return transferShares(undone.outcome.data, accounts, { ...next, id: record.id })!;
}
